import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SmoothLink as Link } from "@/components/navigation";

import { PageHeader } from "@/components/shared/PageHeader";
import { EntityGrid, EntityGridSkeleton } from "@/components/shared/EntityGrid";
import { HospitalCard } from "@/components/hospitals/HospitalCard";
import { useHospitals } from "@/hooks/useHospitals";
import { Button } from "@/components/ui/button";
import { MapPin, MessageCircle } from "lucide-react";
import { AIAgentCard } from "@/components/ai";
import { getWhatsAppLink } from "@/config/company";
import { SEOHead } from "@/components/seo";
import { SEO_METADATA } from "@/lib/seo-metadata";

import { COMPANY } from "@/config/company";

const cityLabels: Record<string, string> = {
  delhi: "Delhi",
  gurugram: "Gurugram",
  mumbai: "Mumbai",
  bangalore: "Bangalore",
};

export default function Hospitals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cityFilter = searchParams.get("city");
  
  const { data: hospitals = [], isLoading } = useHospitals();

  // Get unique cities from hospitals data
  const availableCities = useMemo(() => {
    const cities = [...new Set(hospitals.map(h => h.city.toLowerCase()))];
    return cities.sort();
  }, [hospitals]);

  // Filter hospitals by selected city
  const filteredHospitals = useMemo(() => {
    if (!cityFilter || cityFilter === "any") return hospitals;
    return hospitals.filter(h => h.city.toLowerCase() === cityFilter.toLowerCase());
  }, [hospitals, cityFilter]);

  const handleCityFilter = (city: string | null) => {
    if (city) {
      setSearchParams({ city });
    } else {
      setSearchParams({});
    }
  };

  const pageTitle = cityFilter && cityFilter !== "any" 
    ? `Hospitals in ${cityLabels[cityFilter] || cityFilter.charAt(0).toUpperCase() + cityFilter.slice(1)}`
    : "Partner Hospitals";

  const pageDescription = cityFilter && cityFilter !== "any"
    ? `JCI & NABH accredited hospitals in ${cityLabels[cityFilter] || cityFilter} with world-class facilities`
    : "JCI & NABH accredited hospitals with world-class facilities and expert medical teams";

  const canonicalUrl = `${COMPANY.website}/hospitals/`;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={cityFilter && cityFilter !== "any" 
          ? `${cityLabels[cityFilter] || cityFilter} Hospitals India`
          : SEO_METADATA.hospitals.title}
        description={pageDescription}
        canonicalPath="/hospitals/"
        keywords={SEO_METADATA.hospitals.keywords}
        noindex={!!cityFilter && cityFilter !== "any"}
      />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <PageHeader
            title={pageTitle}
            description={pageDescription}
          />

          {/* City Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              variant={!cityFilter ? "default" : "outline"}
              onClick={() => handleCityFilter(null)}
              className="rounded-full"
            >
              All Cities
            </Button>
            {availableCities.map((city) => (
              <Button
                key={city}
                variant={cityFilter === city ? "default" : "outline"}
                onClick={() => handleCityFilter(city)}
                className="rounded-full gap-1"
              >
                <MapPin className="w-3 h-3" />
                {cityLabels[city] || city.charAt(0).toUpperCase() + city.slice(1)}
              </Button>
            ))}
          </div>


          {isLoading ? (
            <EntityGridSkeleton count={4} columns={2} aspectRatio="video" />
          ) : filteredHospitals.length === 0 ? (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <p className="text-muted-foreground text-lg mb-2">We're expanding our hospital network in this city.</p>
                <p className="text-muted-foreground">Please check back soon or explore hospitals in other locations – we partner with top-tier facilities across India!</p>
              </div>
            </div>
          ) : (
            <>
              <h2 className="sr-only">Available Hospitals</h2>
              <EntityGrid columns={2}>
                {filteredHospitals.map((hospital) => (
                  <HospitalCard key={hospital.id} hospital={hospital} />
                ))}
              </EntityGrid>
            </>
          )}

          {/* Sources & Citations */}
          <div className="mt-12 max-w-4xl mx-auto">
            <h2 className="font-display text-xl font-semibold text-secondary mb-3">
              Sources & Citations
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Accreditation references are provided below. Hospital-specific accreditations should be verified directly
              with each facility.
            </p>
            <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-2">
              <li>
                <a
                  href="https://www.jointcommissioninternational.org/what-we-offer/accreditation/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-primary hover:underline"
                >
                  JCI accreditation overview (Joint Commission International)
                </a>
              </li>
              <li>
                <a
                  href="https://nabh.co/faq-nabh/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-primary hover:underline"
                >
                  NABH overview and recognition (NABH)
                </a>
              </li>
            </ul>
          </div>

          {/* Context-Aware AI Assistant with WhatsApp CTA */}
          <div className="mt-12 space-y-4">
            <AIAgentCard 
              context={cityFilter ? `hospitals in ${cityLabels[cityFilter] || cityFilter}` : "finding the right hospital"}
              title="Talk to Dr. Maya About Hospitals"
              description={cityFilter 
                ? `Get personalized hospital recommendations in ${cityLabels[cityFilter] || cityFilter}. I'll help you find the perfect match for your treatment needs.`
                : "Let me help you choose the right hospital based on your treatment, budget, and preferences."
              }
              quickPrompts={[
                "Recommend a hospital for me",
                "Compare hospital amenities",
                "Get a free quote"
              ]}
            />
            
            {/* Alternative Contact Options */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 p-4 bg-muted/50 rounded-xl border border-border">
              <p className="text-sm text-muted-foreground text-center sm:text-left">
                Need personalized assistance? Our care team is ready to help.
              </p>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm" className="rounded-full">
                  <Link to="/contact/">Contact Us</Link>
                </Button>
                <Button 
                  asChild 
                  size="sm" 
                  className="rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white gap-1.5"
                >
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
}
